import { useQuery, useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { Gift, Copy, Users, CheckCircle2, Clock, Share2, MessageCircle, Link2, UserPlus } from "lucide-react";
import { SiWhatsapp } from "react-icons/si";
import { KudozIcon } from "@/components/KudozIcon";

type Referral = {
  id: string;
  referralCode: string;
  refereeId: string | null;
  status: 'pending' | 'completed' | 'expired';
  referrerBonusKudoz: number;
  refereeBonusKudoz: number;
  createdAt: string;
  completedAt: string | null;
};

type ReferralStats = {
  totalReferrals: number;
  completedReferrals: number;
  totalBonusKudoz: number;
};

type InvitationStats = {
  totalInvites: number;
  byMethod: {
    share_link: number;
    whatsapp: number;
    sms: number;
    contacts: number;
    native_share?: number;
  };
  conversions: number;
  conversionRate: number;
};

type RewardsConfig = {
  referrerSignupBonus: number;
  referrerFirstOrderBonus: number;
  referredUserBonus: number;
  totalReferrerBonus: number;
};

export default function Referrals() {
  const { toast } = useToast();
  const [redeemCode, setRedeemCode] = useState('');

  const { data: referrals, isLoading } = useQuery<Referral[]>({
    queryKey: ['/api/referrals/my'],
  });

  const { data: stats } = useQuery<ReferralStats>({
    queryKey: ['/api/referrals/stats'],
  });

  const { data: invitationStats } = useQuery<InvitationStats>({
    queryKey: ['/api/referrals/invitations/stats'],
  });

  const { data: rewardsConfig } = useQuery<RewardsConfig>({
    queryKey: ['/api/referrals/rewards-config'],
  });

  const referrerBonus = rewardsConfig?.totalReferrerBonus || 150;
  const referredUserBonus = rewardsConfig?.referredUserBonus || 25;

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest('POST', '/api/referrals/create', {});
      return await response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/referrals/my'] });
      queryClient.invalidateQueries({ queryKey: ['/api/referrals/stats'] });
      toast({
        title: "Referral Code Created",
        description: "Share your code with friends to earn Kudoz!",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create referral code",
        variant: "destructive",
      });
    },
  });

  const redeemMutation = useMutation({
    mutationFn: async (code: string) => {
      const response = await apiRequest('POST', '/api/referrals/redeem', { code });
      return await response.json();
    },
    onSuccess: (data: { referral: Referral; referrerBonus: number; refereeBonus: number }) => {
      queryClient.invalidateQueries({ queryKey: ['/api/user'] });
      queryClient.invalidateQueries({ queryKey: ['/api/referrals/my'] });
      queryClient.invalidateQueries({ queryKey: ['/api/referrals/stats'] });
      toast({
        title: "Referral Redeemed!",
        description: `You earned ${data.refereeBonus} Kudoz!`,
      });
      setRedeemCode('');
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to redeem referral code",
        variant: "destructive",
      });
    },
  });

  const copyToClipboard = (code: string) => {
    navigator.clipboard.writeText(code);
    toast({
      title: "Copied!",
      description: "Referral code copied to clipboard",
    });
  };

  const myReferralCode = referrals && referrals.length > 0 ? referrals[0].referralCode : null;

  const getReferralUrl = (code: string) => {
    return `${window.location.origin}/?ref=${code}`;
  };

  const getShareMessage = (code: string) => {
    return `Discover great food on Eatzy Kudoz! Join with my referral code: ${code} and get ${referredUserBonus} bonus Kudoz: ${getReferralUrl(code)}`;
  };

  const trackInvitation = async (method: string) => {
    try {
      await apiRequest('POST', '/api/referrals/invitations', { method });
    } catch (e) {
      // Silent fail - tracking is not critical
    }
  };

  const copyReferralLink = async (code: string) => {
    const url = getReferralUrl(code);
    await navigator.clipboard.writeText(url);
    await trackInvitation('share_link');
    toast({
      title: "Link Copied!",
      description: "Share this link with your friends",
    });
  };

  const shareViaWhatsApp = async (code: string) => {
    const message = encodeURIComponent(getShareMessage(code));
    await trackInvitation('whatsapp');
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  const shareViaSMS = async (code: string) => {
    const message = encodeURIComponent(getShareMessage(code));
    await trackInvitation('sms');
    window.open(`sms:?body=${message}`, '_blank');
  };

  const shareViaWebShare = async (code: string) => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Join me on Eatzy!',
          text: getShareMessage(code),
          url: getReferralUrl(code),
        });
        await trackInvitation('native_share');
      } catch (e) {
        // User cancelled or share failed
      }
    } else {
      // Fallback to copying link - don't track here to avoid double-counting 
      // if user later clicks "Copy Link" button which also tracks
      const url = getReferralUrl(code);
      await navigator.clipboard.writeText(url);
      toast({
        title: "Link Copied!",
        description: "Share this link with your friends",
      });
    }
  };

  return (
    <div className="container max-w-4xl mx-auto p-4 space-y-6">
      <div className="flex items-center gap-3">
        <Gift className="w-8 h-8 text-primary" data-testid="icon-gift" />
        <div>
          <h1 className="text-3xl font-bold" data-testid="text-title">Refer & Earn</h1>
          <p className="text-muted-foreground" data-testid="text-subtitle">
            Invite friends and earn {referrerBonus} Kudoz per referral!
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="card-total-referrals">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Referrals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-muted-foreground" />
                <span className="text-2xl font-bold" data-testid="text-total-referrals">{stats.totalReferrals}</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-completed-referrals">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Completed</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" />
                <span className="text-2xl font-bold" data-testid="text-completed-referrals">{stats.completedReferrals}</span>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="card-bonus-kudoz">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Bonus</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <KudozIcon size="md" />
                <span className="text-2xl font-bold text-primary" data-testid="text-bonus-kudoz">
                  {stats.totalBonusKudoz.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Invitation Metrics Dashboard */}
      {invitationStats && invitationStats.totalInvites > 0 && (
        <Card data-testid="card-invitation-metrics">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Invitation Metrics
            </CardTitle>
            <CardDescription>Track how your sharing efforts are performing</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Metrics Overview */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-3 rounded-md bg-muted/50">
                <p className="text-2xl font-bold" data-testid="text-total-invites">{invitationStats.totalInvites}</p>
                <p className="text-sm text-muted-foreground">Invites Sent</p>
              </div>
              <div className="text-center p-3 rounded-md bg-muted/50">
                <p className="text-2xl font-bold" data-testid="text-conversions">{invitationStats.conversions}</p>
                <p className="text-sm text-muted-foreground">Conversions</p>
              </div>
              <div className="text-center p-3 rounded-md bg-muted/50 col-span-2 md:col-span-2">
                <p className="text-2xl font-bold" data-testid="text-conversion-rate">
                  {invitationStats.conversionRate.toFixed(1)}%
                </p>
                <p className="text-sm text-muted-foreground">Conversion Rate</p>
              </div>
            </div>

            {/* Breakdown by Method */}
            <div>
              <p className="text-sm font-medium mb-2">Breakdown by Method</p>
              <div className="space-y-2">
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Link2 className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Link Shares</span>
                  </div>
                  <Badge variant="secondary" data-testid="badge-link-count">{invitationStats.byMethod.share_link}</Badge>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    <SiWhatsapp className="w-4 h-4 text-green-500" />
                    <span className="text-sm">WhatsApp</span>
                  </div>
                  <Badge variant="secondary" data-testid="badge-whatsapp-count">{invitationStats.byMethod.whatsapp}</Badge>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">SMS</span>
                  </div>
                  <Badge variant="secondary" data-testid="badge-sms-count">{invitationStats.byMethod.sms}</Badge>
                </div>
                <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm">Contacts</span>
                  </div>
                  <Badge variant="secondary" data-testid="badge-contacts-count">{invitationStats.byMethod.contacts}</Badge>
                </div>
                {(invitationStats.byMethod.native_share ?? 0) > 0 && (
                  <div className="flex items-center justify-between p-2 rounded-md bg-muted/30">
                    <div className="flex items-center gap-2">
                      <Share2 className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">Other Shares</span>
                    </div>
                    <Badge variant="secondary" data-testid="badge-native-share-count">{invitationStats.byMethod.native_share}</Badge>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Your Referral Code */}
      <Card data-testid="card-your-code">
        <CardHeader>
          <CardTitle>Your Referral Code</CardTitle>
          <CardDescription>
            Share this code with friends. They get {referredUserBonus} Kudoz, you get {referrerBonus} Kudoz when they sign up!
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {myReferralCode ? (
            <>
              <div className="flex gap-2">
                <Input
                  value={myReferralCode}
                  readOnly
                  className="text-lg font-bold text-center"
                  data-testid="input-referral-code"
                />
                <Button
                  onClick={() => copyToClipboard(myReferralCode)}
                  variant="outline"
                  data-testid="button-copy-code"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
              
              {/* Share Buttons */}
              <div className="pt-2">
                <p className="text-sm text-muted-foreground mb-3">Share with friends</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <Button
                    variant="outline"
                    onClick={() => copyReferralLink(myReferralCode)}
                    className="flex items-center gap-2"
                    data-testid="button-share-link"
                  >
                    <Link2 className="w-4 h-4" />
                    <span>Copy Link</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => shareViaWhatsApp(myReferralCode)}
                    className="flex items-center gap-2"
                    data-testid="button-share-whatsapp"
                  >
                    <SiWhatsapp className="w-4 h-4 text-green-500" />
                    <span>WhatsApp</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => shareViaSMS(myReferralCode)}
                    className="flex items-center gap-2"
                    data-testid="button-share-sms"
                  >
                    <MessageCircle className="w-4 h-4" />
                    <span>SMS</span>
                  </Button>
                  
                  <Button
                    variant="outline"
                    onClick={() => shareViaWebShare(myReferralCode)}
                    className="flex items-center gap-2"
                    data-testid="button-share-more"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>More</span>
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending}
              className="w-full"
              data-testid="button-create-code"
            >
              {createMutation.isPending ? "Creating..." : "Generate Referral Code"}
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Redeem a Code */}
      <Card data-testid="card-redeem">
        <CardHeader>
          <CardTitle>Have a Referral Code?</CardTitle>
          <CardDescription>Enter a friend's code to earn {referredUserBonus} bonus Kudoz</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Input
              placeholder="Enter code"
              value={redeemCode}
              onChange={(e) => setRedeemCode(e.target.value.toUpperCase())}
              data-testid="input-redeem-code"
            />
            <Button
              onClick={() => redeemMutation.mutate(redeemCode)}
              disabled={!redeemCode || redeemMutation.isPending}
              data-testid="button-redeem"
            >
              {redeemMutation.isPending ? "Redeeming..." : "Redeem"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card data-testid="card-history">
        <CardHeader>
          <CardTitle>Referral History</CardTitle>
          <CardDescription>Track your successful referrals</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 p-3 rounded-md">
                  <Skeleton className="w-10 h-10" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                  <Skeleton className="h-6 w-20" />
                </div>
              ))
            ) : referrals && referrals.length > 0 ? (
              referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center gap-4 p-3 rounded-md hover-elevate"
                  data-testid={`row-referral-${referral.id}`}
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    {referral.status === 'completed' ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" data-testid={`icon-completed-${referral.id}`} />
                    ) : (
                      <Clock className="w-5 h-5 text-muted-foreground" data-testid={`icon-pending-${referral.id}`} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="font-semibold" data-testid={`text-code-${referral.id}`}>{referral.referralCode}</p>
                    <p className="text-sm text-muted-foreground">
                      {new Date(referral.createdAt).toLocaleDateString()}
                    </p>
                  </div>

                  <div className="text-right">
                    <Badge
                      variant={referral.status === 'completed' ? 'default' : 'secondary'}
                      data-testid={`badge-status-${referral.id}`}
                    >
                      {referral.status === 'completed' ? `+${referral.referrerBonusKudoz}` : 'Pending'}
                    </Badge>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground" data-testid="text-no-referrals">
                No referrals yet. Create your code and start sharing!
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
